import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { AuthorBox } from "@/components/AuthorBox";
import { ProofLink } from "@/components/ProofLink";
import { ReviewedBy } from "@/components/ReviewedBy";
import { ArticleBody } from "@/components/article/ArticleBody";
import { ArticleByline } from "@/components/article/ArticleByline";
import { ArticleLinkPanel } from "@/components/article/ArticleLinkPanel";
import { AeoSeries, AeoServiceCta } from "@/components/article/AeoArticleExtras";
import { POSTS, isDraftPost, loadPost } from "@/content/posts";
import { BN_POSTS } from "@/content/bn/posts";
import { SITE } from "@/content/site";
import { resolveInternalLinks, GROUP_ORDER } from "@/content/internal-links";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { faqSchema } from "@/lib/schema";
import { articleAuthor, articlePublisher, articleReviewer } from "@/lib/article-schema";
import { articleTakeaways } from "@/lib/article-takeaways";

/** The Bangla post that declares this English post as its pair, if any. */
function bnCounterpart(slug: string) {
  return BN_POSTS.find((b) => b.enPath === `/blog/${slug}`);
}

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    // Full body is loaded on demand so article content stays out of the
    // shared client entry chunk.
    const p = await loadPost(params.slug);
    if (!p) throw notFound();
    return { p };
  },
  head: ({ loaderData }) => {
    const p = loaderData?.p;
    if (!p) return { meta: [{ title: "Post Not Found" }] };
    return buildSeo({
      title: `${p.title} | Freelancer Tamal`,
      description: p.excerpt,
      path: `/blog/${p.slug}`,
      type: "article",
      publishedTime: p.date,
      modifiedTime: p.updated ?? p.date,
      keywords: `${p.category}, SEO, ${p.title}`,
      ogEyebrow: p.category,
      ogTitle: p.title,
      // Unreleased drafts stay reachable by URL for review but never enter the index.
      noindex: isDraftPost(p.slug),
      // Reciprocal hreflang when a Bangla counterpart declares this post as its
      // English pair (see `enPath` in src/content/bn/posts.ts).
      alternates: (() => {
        const bn = bnCounterpart(p.slug);
        return bn ? { bn: `/bn/blog/${bn.slug}` } : undefined;
      })(),
    });
  },
  component: Post,
  notFoundComponent: () => (
    <div className="container-x py-32 text-center">
      <h1 className="font-display text-4xl">Post not found</h1>
      <Link to="/blog" className="mt-6 inline-flex text-primary">← All articles</Link>
    </div>
  ),
});

function Post() {
  const { p } = Route.useLoaderData();
  const url = `${SITE.url}/blog/${p.slug}`;
  const takeaways = articleTakeaways(p);
  const related = POSTS.filter((x) => x.slug !== p.slug && x.category === p.category).slice(0, 2);
  // Collect unique outbound citations for Article JSON-LD `citation`.
  const citations = Array.from(new Set(p.body.flatMap((b) => (b.links ?? []).map((l) => l.href))));
  const bn = bnCounterpart(p.slug);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: p.title, path: `/blog/${p.slug}` },
  ];

  return (
    <>
      <Breadcrumbs items={crumbs} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${url}#article`,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        headline: p.title,
        description: p.excerpt,
        author: articleAuthor(),
        reviewedBy: articleReviewer(),
        publisher: articlePublisher(),
        datePublished: p.date,
        dateModified: p.updated ?? p.date,
        inLanguage: "en",
        articleSection: p.category,
        keywords: [p.category, "SEO", p.title].join(", "),
        ...(citations.length > 0 ? { citation: citations } : {}),
        // Points at the Bangla translation so both versions resolve to one work.
        ...(bn ? { workTranslation: { "@id": `${SITE.url}/bn/blog/${bn.slug}#article` } } : {}),
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", ".aeo-speakable"],
        },
        wordCount: p.body
          .map((b) => (b.h ?? "") + " " + (b.p ?? "") + " " + (b.a ?? ""))
          .join(" ")
          .trim()
          .split(/\s+/)
          .length,
      }} />
      <JsonLd data={breadcrumbSchema(crumbs)} />
      {p.faq && p.faq.length > 0 && (
        <JsonLd data={{ ...faqSchema(p.faq), "@id": `${url}#faq` }} />
      )}

      {p.howTo && (
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: p.howTo.name,
            description: p.howTo.description,
            ...(p.howTo.totalTime ? { totalTime: p.howTo.totalTime } : {}),
            step: p.howTo.steps.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.name,
              text: s.text,
              ...(s.url ? { url: s.url } : {}),
            })),
          }}
        />
      )}

      <article className="container-x pt-12 pb-16 max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> All articles
        </Link>

        <div className="mt-12">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em]">
            <span className="text-primary">{p.category}</span>
            <span className="text-muted-foreground">· {p.readMin} min · {new Date(p.date).toLocaleDateString("en", { year: "numeric", month: "long", day: "numeric" })}</span>
            {p.updated && p.updated !== p.date && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 normal-case tracking-normal text-[11px] text-primary">
                <Clock size={11} /> Updated {new Date(p.updated).toLocaleDateString("en", { year: "numeric", month: "short", day: "numeric" })}
              </span>
            )}
          </div>
          <h1 className="mt-6 font-display text-4xl md:text-6xl leading-[1.05]">{p.title}</h1>
          <p className="mt-8 text-xl text-muted-foreground">{p.excerpt}</p>

          <KeyTakeaways items={takeaways} />

          <ReviewedBy reviewedOn={p.updated ?? p.date} />

          <ArticleByline />

          <ArticleBody blocks={p.body} lang="en" />

          <div className="mt-20 pt-10 border-t border-border">
            <h2 className="font-display text-3xl text-foreground">Frequently asked</h2>
            <div className="mt-8 space-y-6">
              {p.faq.map((f) => (
                <div key={f.q} className="rounded-2xl border border-border p-6">
                  <div className="font-medium text-foreground">{f.q}</div>
                  <p className="mt-3 text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </div>

          <ArticleLinkPanel
            links={resolveInternalLinks(p.internalLinks)}
            groupOrder={GROUP_ORDER}
            eyebrow="Continue your research"
            title="Related services, guides & deep-dives"
            className="mt-16 rounded-3xl border border-border bg-card/50 p-8 lg:p-10"
            router
          />

          {p.category === "AEO" && <AeoServiceCta />}

          <ProofLink subjectPath={`/blog/${p.slug}`} />

          <AuthorBox />

          {p.category === "AEO" && <AeoSeries currentSlug={p.slug} />}
        </div>
      </article>

      {related.length > 0 && (
        <Section eyebrow="Keep reading" title="More on this topic.">
          <div className="grid md:grid-cols-2 gap-6">
            {related.map((r) => (
              <Link key={r.slug} to="/blog/$slug" params={{ slug: r.slug }} className="group rounded-3xl border border-border p-8 hover:bg-card transition">
                <div className="text-xs uppercase tracking-[0.18em] text-primary">{r.category}</div>
                <h3 className="mt-4 font-display text-2xl">{r.title}</h3>
                <ArrowUpRight size={16} className="mt-6 text-muted-foreground group-hover:text-primary transition" />
              </Link>
            ))}
          </div>
        </Section>
      )}
    </>
  );
}
